import {
  RECIPIENT,
  type ChatQuestion,
  type ReviewRow,
} from '@/karehero-app/tell-us-once/intake/content';

// CONCEPT. The Attendance Allowance claim from the Joining up hub, asked in the
// calculator's chat (AA Experiment in Notion). Only the screens the demo needs:
// the six stages, the start of the claim, part of Health and its read-back,
// and consent on screen. The other stages' questions aren't here.

const R = RECIPIENT;

/**
 * The claim in five stages, the way progress is shown: stages, never a count
 * of the form's 65 questions. The spec's triage folds into the first; consent,
 * bank details and the declaration happen on screen as they come up.
 */
export const AA_STAGES = [
  `About ${R}`,
  `${R}'s health`,
  `Where ${R} lives`,
  `${R}'s care, day and night`,
  'Anything else',
];

/** What each stage asks, a line each, for the drawer before starting. */
export const AA_STAGE_LINES = [
  'Name, date of birth and National Insurance number',
  `Conditions, medicines and who ${R} sees`,
  'The home, and any time in hospital',
  `The help ${R} needs, in your own words`,
  "Anything the form hasn't covered",
];

export const AA_PROGRESS = {
  sheet: 'Your claim',
  /** To trial in the bar, in place of the stage and segments. */
  percent: (n: number) => `${n}% complete`,
  /** Over the stages on Money, so the list reads as what's to do. */
  heading: "What we'll ask about",
  intro:
    "You'll need to answer questions about the following to complete your claim.",
  done: 'Done',
  current: 'In progress',
  /**
   * The Money card's chip: a status, not a count, which means nothing on its
   * own there. The drawer says which stages are done.
   */
  chip: (done: number) => (done === 0 ? 'Not started' : 'In progress'),
  /** The drawer's way in, before and after starting. */
  start: 'Start the claim',
  resume: 'Carry on',
};

/** Which stage each question belongs to, for the bar. */
export const AA_STAGE_OF: Record<string, number> = {
  'special-rules': 0,
  about: 0,
  upset: 1,
  stop: 1,
  conditions: 1,
  'how-long': 1,
  medicines: 1,
  gp: 1,
  consent: 1,
  'consent-again': 1,
  'health-readback': 1,
  home: 2,
  back: 2,
};

/**
 * Margaret's claim, from the start. About Margaret is what onboarding gave
 * us, said back to check. Health confirms what we hold, asks the rest, offers a
 * photo where one is quicker, and ends with a read-back.
 */
export const AA_CHAT: ChatQuestion[] = [
  // Triage: special rules first, because a yes skips most of the care
  // questions and needs a form from the doctor (SR1). Asked gently, and of
  // everyone, so nobody has to bring it up.
  {
    id: 'special-rules',
    says: [
      `Let's start ${R}'s Attendance Allowance claim. You can stop whenever you like, and it keeps what you've answered.`,
    ],
    ask: `First, has a doctor said ${R} may be nearing the end of life? If so, there's a shorter way to claim.`,
    mode: 'say',
    typed: true,
    answer: 'No',
  },
  {
    id: 'about',
    says: ["Thanks. Here's what we already have."],
    card: {
      label: 'Does this look right?',
      items: [
        'Margaret Ellen Doyle',
        'Born 19 March 1943',
        '22 Elm Road, Leeds LS6 2AB',
        `You're filling it in for ${R}`,
      ],
      editable: true,
    },
    ask: '',
    mode: 'say',
    typed: true,
    answer: "Yes, that's right",
  },
  {
    id: 'conditions',
    says: [`Thanks, that's the first part done. Next, ${R}'s health.`],
    ask: "I've got arthritis and early dementia. Is there anything else?",
    mode: 'say',
    typed: true,
    answer: 'Angina as well',
  },
  {
    id: 'how-long',
    ask: `How long has ${R} had arthritis?`,
    mode: 'say',
    typed: true,
    answer: 'About ten years',
  },
  {
    id: 'medicines',
    ask: `Is anything prescribed for these? A photo of ${R}'s prescription list is often quicker than going through each one.`,
    mode: 'say',
    typed: true,
    answer: "Here's the list",
  },
  {
    id: 'gp',
    says: ['Thanks. I can see donepezil, co-codamol and a GTN spray.'],
    ask: `When did ${R} last see the GP at Elm Road surgery?`,
    mode: 'say',
    typed: true,
    answer: 'July, I think',
  },
  {
    // Question 20. Said like any other answer: a yes or no needs no options.
    // Only a vague answer is asked again (AA_CHAT_VAGUE).
    id: 'consent',
    ask: `DWP may want to check details with ${R}'s GP. Is that OK? If you say no, DWP may not be able to check the claim.`,
    mode: 'say',
    typed: true,
    answer: "Yes, that's fine",
  },
  {
    id: 'health-readback',
    says: ["Here's what I've got."],
    card: {
      label: `${R}'s health`,
      items: [
        'Arthritis, for about ten years',
        'Early dementia',
        'Angina',
        'Donepezil, co-codamol and a GTN spray',
        'GP at Elm Road surgery, last seen in July. The day needs checking',
      ],
      editable: true,
    },
    ask: 'Is anything wrong or missing?',
    mode: 'say',
    typed: true,
    answer: "That's right",
  },
  {
    id: 'home',
    says: [`Thanks, that's ${R}'s health done.`],
    ask: `Next, where ${R} lives. Does ${R} live at home?`,
    mode: 'say',
    typed: true,
    answer: 'Yes',
  },
];

/**
 * The same claim, with a vague answer to consent. A maybe is never taken as
 * consent, so it's asked once more, plainly, then on as before.
 */
const consentAt = AA_CHAT.findIndex((q) => q.id === 'consent');
export const AA_CHAT_VAGUE: ChatQuestion[] = [
  ...AA_CHAT.slice(0, consentAt),
  { ...AA_CHAT[consentAt], answer: 'I suppose so' },
  {
    id: 'consent-again',
    // Asked again because the answer was vague, so this time the yes and no
    // are there to tap: the one place consent shows its answers.
    ask: 'Just to check, is that a yes? DWP needs a clear yes or no for this one.',
    mode: 'say',
    attachRows: {},
    asides: [{ label: 'Yes' }, { label: 'No' }],
    answer: 'Yes',
  },
  ...AA_CHAT.slice(consentAt + 1),
];

/**
 * The same claim, with the caller upset partway through Health. The questions
 * stop, it's acknowledged in a line, and stopping is offered: everything is
 * saved and picks up from the same question next time.
 */
const medicinesAt = AA_CHAT.findIndex((q) => q.id === 'medicines');
export const AA_CHAT_DISTRESS: ChatQuestion[] = [
  ...AA_CHAT.slice(0, medicinesAt),
  { ...AA_CHAT[medicinesAt], answer: "Sorry, it's just a lot at the moment." },
  {
    id: 'upset',
    ask: "That sounds really hard. There's no rush. We can take a break, and everything so far is saved.",
    mode: 'say',
    typed: true,
    answer: 'Can we stop for today?',
  },
  {
    id: 'stop',
    ask: `Of course. Next time we'll pick up from ${R}'s medicines. Take care.`,
    mode: 'say',
    next: { '*': 'end' },
  },
];

/**
 * What each stage holds so far, by stage, for the claim's answers page. Only
 * stages that are done or in progress; the rest have nothing to show yet.
 */
export const AA_ANSWERS: Record<number, ReviewRow[]> = {
  0: [
    { id: 'name', label: 'Legal name', value: 'Margaret Ellen Doyle' },
    { id: 'dob', label: 'Date of birth', value: '19 March 1943' },
    { id: 'address', label: 'Address', value: '22 Elm Road, Leeds LS6 2AB' },
    { id: 'signing', label: 'Who fills it in', value: `You, for ${R}` },
  ],
  1: [
    {
      id: 'conditions',
      label: 'Conditions',
      value: 'Arthritis, early dementia, angina',
    },
    { id: 'arthritis', label: 'Had arthritis for', value: 'About ten years' },
    {
      id: 'medicines',
      label: 'Medicines',
      value: 'Donepezil, co-codamol, GTN spray',
    },
    { id: 'gp', label: 'GP', value: 'Elm Road surgery' },
    { id: 'gp-seen', label: 'Last saw the GP', value: 'July 2026' },
  ],
  2: [
    { id: 'home', label: 'Lives at home', value: 'Yes' },
    { id: 'home-type', label: 'Type of home', value: '', from: 'missing' },
    {
      id: 'hospital',
      label: 'In hospital or a care home lately',
      value: '',
      from: 'missing',
    },
  ],
};

/** The claim with every stage answered. */
export const AA_ANSWERS_FINISHED: Record<number, ReviewRow[]> = {
  0: AA_ANSWERS[0],
  1: AA_ANSWERS[1],
  2: [
    { id: 'home', label: 'Lives at home', value: 'Yes' },
    { id: 'home-type', label: 'Type of home', value: 'A house, on two floors' },
    {
      id: 'hospital',
      label: 'In hospital or a care home lately',
      value: 'No',
    },
  ],
  3: [
    {
      id: 'mornings',
      label: 'Getting up and going to bed',
      value: 'Needs help most mornings',
    },
    {
      id: 'washing',
      label: 'Washing and dressing',
      value: 'Needs help every day',
    },
    {
      id: 'toilet',
      label: 'Going to the toilet',
      value: 'Needs help at night',
    },
    {
      id: 'meals',
      label: 'Eating and medication',
      value: 'Reminders for every meal and tablet',
    },
    {
      id: 'watching',
      label: 'Being watched over',
      value: "Can't be left alone for long",
    },
    { id: 'nights', label: 'At night', value: 'Up three or four times' },
  ],
  4: [
    {
      id: 'more',
      label: 'Anything else for DWP',
      value: `${R} had a fall in October`,
    },
    {
      id: 'documents',
      label: 'Documents',
      value: 'Prescription list, memory clinic letter',
    },
  ],
};

/**
 * The finished claim reviewed by topic rather than by question: one row per
 * condition, per care task, with what was said summed up in a line. The real
 * form is 227 questions; Care alone can be 60 to 100 answers. The field-by-
 * field answers still sit under these for the care expert.
 */
export const AA_SUMMARY: Record<number, ReviewRow[]> = {
  0: [
    {
      id: 's-who',
      label: 'Who it is for',
      value: 'Margaret Ellen Doyle, born 19 March 1943',
    },
    {
      id: 's-address',
      label: `Where ${R} lives`,
      value: '22 Elm Road, Leeds LS6 2AB',
    },
    {
      id: 's-you',
      label: 'Who fills it in',
      value: `You, for ${R}`,
    },
  ],
  1: [
    {
      id: 's-arthritis',
      label: 'Arthritis',
      value: 'For about ten years. Co-codamol four times a day.',
    },
    {
      id: 's-dementia',
      label: 'Early dementia',
      value:
        "Since spring 2026. Donepezil at night. Seen at St Mary's memory clinic every three months.",
    },
    {
      id: 's-angina',
      label: 'Angina',
      value: 'A GTN spray when needed.',
    },
    { id: 's-gp', label: 'GP', value: 'Elm Road surgery' },
  ],
  2: [
    {
      id: 's-home',
      label: 'Home',
      value: 'A house on two floors. The toilet is upstairs.',
    },
    {
      id: 's-hospital',
      label: 'Hospital or care home',
      value: 'No stays lately.',
    },
  ],
  3: [
    {
      id: 's-mornings',
      label: 'Getting up and going to bed',
      value: 'Needs help most mornings, about 15 minutes.',
    },
    {
      id: 's-washing',
      label: 'Washing and dressing',
      value: 'Needs help every day with the shower and buttons.',
    },
    {
      id: 's-toilet',
      label: 'Going to the toilet',
      value: 'Managing in the day. Needs help at night.',
    },
    {
      id: 's-meals',
      label: 'Eating and medication',
      value: 'Reminders for every meal and every tablet.',
    },
    {
      id: 's-moving',
      label: 'Getting around',
      value: 'A hand on the stairs. Had a fall in October.',
    },
    {
      id: 's-watching',
      label: 'Being watched over',
      value: "Can't be left alone for more than an hour.",
    },
    {
      id: 's-nights',
      label: 'At night',
      value: 'Up three or four times, needs help each time.',
    },
  ],
  4: [
    {
      id: 's-more',
      label: 'Anything else for DWP',
      value: 'The fall in October, and how the nights have changed.',
    },
    {
      id: 's-docs',
      label: 'Documents',
      value: 'Prescription list, memory clinic letter',
    },
  ],
};

/**
 * The few answers that still need them, first: missing, or not exact enough
 * for the form. Everything else is confirmed and stays quiet.
 */
export const AA_TO_CHECK = {
  heading: 'To check',
  rows: [
    {
      // stage is which of AA_STAGES it belongs to, for the stepped review.
      id: 'c-ni',
      label: 'National Insurance number',
      value: '',
      from: 'missing',
      stage: 0,
    },
    {
      id: 'c-gp',
      label: `When ${R} last saw the GP`,
      value: 'July 2026',
      from: 'missing',
      note: 'DWP needs the day as well.',
      stage: 1,
    },
    {
      // Read from their photo, and the reading wasn't sure: checked like a
      // gap, before the rest.
      id: 'c-medicines',
      label: 'Medicines, from your photo',
      value: 'Donepezil 10mg, co-codamol 30/500 and a GTN spray',
      from: 'ai',
      note: "We read this from the photo you sent. Check it's right.",
      stage: 1,
    },
  ] as (ReviewRow & { stage: number })[],
};

/**
 * The claim with every stage answered. Not sent: a care expert goes through it
 * with them first, so the page leads with what it found and the call.
 */
export const AA_FINISHED = {
  chip: 'Ready for a call',
  result: {
    name: `It looks like ${R} is eligible for Attendance Allowance`,
    description: 'Thanks for answering all the questions.',
  },
  call: {
    icon: 'phone',
    name: 'Book a call',
    description:
      'A care expert will check your answers with you, to give the application the best chance.',
  },
};

/**
 * The same page when the answers say no. Not a dead end: a care expert can
 * still go through it and look at other help.
 */
export const AA_NOT_ELIGIBLE = {
  chip: 'Ready for a call',
  result: {
    name: `It looks like ${R} can't get Attendance Allowance right now`,
    description: 'Thanks for answering all the questions.',
  },
  call: {
    icon: 'phone',
    name: 'Book a call',
    description: `A care expert can go through your answers with you and look at other help ${R} could get.`,
  },
};

export const AA_ANSWERS_PAGE = {
  /** On the claim's drawer once started: the way to what's been said. */
  link: 'See your answers',
  carryOn: 'Carry on',
};

/**
 * Coming back part way through, straight into the chat: a conversation of its
 * own that says where they got to, then picks up at the first question left.
 */
export const AA_CHAT_BACK: ChatQuestion[] = [
  {
    id: 'back',
    says: [`Welcome back. You've done ${R}'s details and health.`],
    ask: `Next, where ${R} lives. Does ${R} live at home?`,
    mode: 'say',
    typed: true,
    answer: 'Yes',
  },
];

/** Checking the finished claim a stage at a time, before the result. */
export const AA_REVIEW = {
  subtitle: 'Review your answers',
  /** The first page: everything that needs them, before the rest. */
  first: 'A few things to check first',
  /** On the first page, how the review works. */
  how: "Swipe to move between sections. When you're done, tap Done reviewing.",
  done: 'Done reviewing',
};
